import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import Quill, { type QuillOptions } from "quill";
import "quill/dist/quill.snow.css";
import { BlockEmbed } from "quill/blots/block";
import Toolbar from "quill/modules/toolbar";
import "./RichTextEditor.css";
import type { Delta } from "quill/core";
import { filterDelta } from "@repo/shared/deltaToRichText";
import type { QuillDelta } from "@repo/types";
import { cn } from "@repo/shared/utils";

// Quill divider

class DividerBlot extends BlockEmbed {
  static blotName = "divider";
  static tagName = "hr";
}
Quill.register(DividerBlot);

const postProcessQuill = (quill: Quill) => {
  const toolbar = quill.getModule("toolbar") as Toolbar;
  const divider = toolbar.container?.querySelector(".ql-divider");
  if (divider) {
    divider.innerHTML = `
      <svg viewBox="0 0 18 18">
        <line class="ql-stroke" x1="1.795" y1="9" x2="16.205" y2="9"/>
        <polyline class="ql-stroke" points="13.494,4.706 13.494,6.239 4.506,6.239 4.506,4.706 "/>
        <polyline class="ql-stroke" points="13.494,13.295 13.494,11.76 4.506,11.76 4.506,13.295 "/>
      </svg>
    `;

    toolbar.addHandler("divider", () => {
      const range = quill.getSelection(true);
      const prev = quill.getText({ index: range.index - 1, length: 1 });
      const next = quill.getText({ index: range.index, length: 1 });

      if (prev !== "\n" && prev !== "") {
        quill.insertText(range.index++, "\n", "user");
      }
      quill.insertEmbed(range.index++, "divider", true, "user");
      if (next === "\n") {
        quill.deleteText(range.index, 1, "user");
      }

      quill.setSelection(range.index, 0, "user");
    });
  }
};

// Richtext Editor

export interface RichTextEditorProps {
  className?: string;
  containerStyles?: string;
  options?: QuillOptions;
  contents?: Delta | string;
}

export const RichTextEditor = forwardRef<
  Quill | undefined,
  RichTextEditorProps
>(
  (
    { className, options, containerStyles = "", contents }: RichTextEditorProps,
    ref
  ) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const [quillOptions] = useState({ ...options });
    const [quill, setQuill] = useState<Quill | undefined>();
    const [initialContents] =
      useState<RichTextEditorProps["contents"]>(contents);
    const containerStylesRef = useRef(containerStyles);
    const containerBaseStylesRef = useRef("");
    const editorContainerRef = useRef<HTMLDivElement | null>(null);

    // update editor container styles
    if (
      editorContainerRef.current &&
      containerStylesRef.current !== containerStyles
    ) {
      containerStylesRef.current = containerStyles;
      editorContainerRef.current.className =
        containerBaseStylesRef.current + " " + containerStyles;
    }

    useImperativeHandle(ref, () => quill, [quill]);

    useEffect(() => {
      const container = containerRef.current;
      if (!container) return;

      // setup editor container
      const editorContainer = document.createElement("div");
      container.appendChild(editorContainer);

      editorContainerRef.current = editorContainer;

      const quill = new Quill(editorContainer, {
        theme: "snow",
        ...quillOptions,
      });

      containerBaseStylesRef.current = editorContainer.className;
      if (containerStylesRef.current) {
        editorContainer.className += " " + containerStylesRef.current;
      }

      postProcessQuill(quill);

      quill.clipboard.addMatcher(Node.ELEMENT_NODE, (_node, delta) => {
        return filterDelta(delta.slice() as QuillDelta) as Delta;
      });

      if (typeof initialContents === "string") {
        quill.setContents(
          quill.clipboard.convert({ html: initialContents }),
          "silent"
        );
      } else if (initialContents) {
        quill.setContents(initialContents, "silent");
      }
      quill.history.clear();

      setQuill(quill);

      return () => {
        setQuill(undefined);
        editorContainerRef.current = null;
        container.innerHTML = "";
      };
    }, [containerRef, quillOptions, initialContents]);

    return (
      <div className={cn("richtext-editor", className)} ref={containerRef} />
    );
  }
);
